/**
 * Minimal browser-side ID3v2 (MP3) tag reader.
 *
 * Reads the ID3v2.3 / ID3v2.4 header at the very start of an MP3 file and
 * extracts the three frames we care about for auto-filling the New Map
 * dialog:
 *   - TIT2 → song title
 *   - TPE1 → artist
 *   - APIC → embedded cover art (bytes + MIME)
 *
 * Implemented inline (no external dep) so we don't need another npm package
 * during the manual-install workflow. Returns an empty object for non-MP3
 * files, files without ID3v2, or anything the parser doesn't recognize —
 * the caller falls back to the user filling fields manually.
 */

export interface Mp3Metadata {
  title?: string;
  artist?: string;
  /** Raw bytes of the embedded picture (typically JPEG or PNG). */
  pictureBytes?: ArrayBuffer;
  pictureMime?: string;
}

/**
 * Parse ID3v2 frames at the head of `file`. Only reads what's needed (the
 * tag is sized in its header), so even big MP3s are quick.
 */
export async function extractMp3Metadata(file: File): Promise<Mp3Metadata> {
  // ID3v2 header is exactly 10 bytes: "ID3" + ver + rev + flags + 4-byte synchsafe size.
  const head = new Uint8Array(await file.slice(0, 10).arrayBuffer());
  if (head.length < 10) return {};
  if (head[0] !== 0x49 || head[1] !== 0x44 || head[2] !== 0x33) return {}; // not "ID3"
  const majorVer = head[3];
  if (majorVer !== 3 && majorVer !== 4) return {};

  // Synchsafe integer: each byte's high bit is forced to 0, real value is
  // built from the low 7 bits of each byte (big-endian).
  const tagSize = synchsafe(head[6], head[7], head[8], head[9]);
  if (tagSize <= 0 || tagSize > file.size - 10) return {};

  const tag = new Uint8Array(await file.slice(10, 10 + tagSize).arrayBuffer());
  const out: Mp3Metadata = {};

  let off = 0;
  while (off + 10 <= tag.length) {
    // Frame header: 4-byte ID + 4-byte size + 2-byte flags.
    const id = String.fromCharCode(tag[off], tag[off + 1], tag[off + 2], tag[off + 3]);
    if (!/^[A-Z0-9]{4}$/.test(id)) break; // padding zeros / corrupt
    // Frame size is synchsafe in v2.4, regular big-endian in v2.3.
    const frameSize = majorVer === 4
      ? synchsafe(tag[off + 4], tag[off + 5], tag[off + 6], tag[off + 7])
      : (tag[off + 4] << 24 >>> 0) | (tag[off + 5] << 16) | (tag[off + 6] << 8) | tag[off + 7];
    if (frameSize <= 0 || off + 10 + frameSize > tag.length) break;
    const body = tag.subarray(off + 10, off + 10 + frameSize);

    if (id === 'TIT2') {
      out.title = decodeTextFrame(body);
    } else if (id === 'TPE1') {
      out.artist = decodeTextFrame(body);
    } else if (id === 'APIC') {
      const pic = decodeApicFrame(body);
      if (pic) {
        out.pictureBytes = pic.bytes;
        out.pictureMime = pic.mime;
      }
    }

    off += 10 + frameSize;
  }

  return out;
}

// ── Helpers ────────────────────────────────────────────────────────────────

function synchsafe(b0: number, b1: number, b2: number, b3: number): number {
  return ((b0 & 0x7f) << 21) | ((b1 & 0x7f) << 14) | ((b2 & 0x7f) << 7) | (b3 & 0x7f);
}

/** Text frames lead with a 1-byte encoding marker, then the string. */
function decodeTextFrame(body: Uint8Array): string {
  if (body.length === 0) return '';
  const encoding = body[0];
  const data = body.subarray(1);
  let text: string;
  try {
    if (encoding === 0) {
      text = new TextDecoder('iso-8859-1').decode(data);
    } else if (encoding === 1) {
      // UTF-16 with BOM (FE FF or FF FE).
      text = new TextDecoder('utf-16').decode(data);
    } else if (encoding === 2) {
      text = new TextDecoder('utf-16be').decode(data);
    } else if (encoding === 3) {
      text = new TextDecoder('utf-8').decode(data);
    } else {
      return '';
    }
  } catch {
    return '';
  }
  // Strip nulls / control chars at the tail.
  return text.replace(/[\x00\r\n]+$/g, '').trim();
}

/**
 * APIC payload layout:
 *   [1 byte encoding][NUL-terminated ASCII MIME][1 byte picture type]
 *   [terminated description][image bytes]
 *
 * Description terminator depends on the encoding (single NUL for ASCII/UTF-8,
 * double NUL for UTF-16 variants).
 */
function decodeApicFrame(body: Uint8Array): { bytes: ArrayBuffer; mime: string } | null {
  if (body.length < 4) return null;
  const encoding = body[0];
  let i = 1;

  // MIME — ASCII, null-terminated.
  let mimeEnd = i;
  while (mimeEnd < body.length && body[mimeEnd] !== 0) mimeEnd++;
  if (mimeEnd >= body.length) return null;
  const mime = new TextDecoder('ascii').decode(body.subarray(i, mimeEnd)) || 'image/jpeg';
  i = mimeEnd + 1;

  // Picture type — 1 byte (we don't care which kind, just take whatever).
  if (i >= body.length) return null;
  i += 1;

  // Description — terminated by 1 NUL (ASCII / UTF-8) or 2 NULs (UTF-16).
  if (encoding === 1 || encoding === 2) {
    while (i + 1 < body.length && !(body[i] === 0 && body[i + 1] === 0)) i += 1;
    i += 2;
  } else {
    while (i < body.length && body[i] !== 0) i += 1;
    i += 1;
  }
  if (i >= body.length) return null;

  // Remainder is the actual picture data. Clone into a standalone ArrayBuffer
  // so the caller can stash it without keeping the whole tag alive.
  const slice = body.subarray(i);
  const out = new ArrayBuffer(slice.byteLength);
  new Uint8Array(out).set(slice);
  return { bytes: out, mime };
}

/**
 * Best-effort parse of a song filename into (artist, title).
 *
 * Real-world filenames are messy — they come from YouTube downloaders,
 * Spotify dumps, ripped CDs and random sources. We strip the noise that
 * usually surrounds the actual song info and then split on the most
 * reliable separator: " - " (or its em-/en-dash variants).
 *
 * Pipeline:
 *   1. Strip the file extension.
 *   2. Strip leading track numbers ("01 - ", "12. ").
 *   3. Trim parenthesised qualifiers ("(Lyrics)", "(Official Video)", …)
 *      *and everything after them* — videos often have lyric snippets
 *      appended to the filename which would otherwise poison the title.
 *   4. Split on " - " / " – " / " — ". With more than 2 chunks, use the
 *      last two as (artist, title) — the leading bits are usually the
 *      channel name from the source.
 *
 * Returns whatever it could recover; callers decide if any is good
 * enough to act on.
 */
export function parseFilenameMetadata(
  filename: string,
): { artist?: string; title?: string } {
  let s = filename;

  // 1. Strip extension
  s = s.replace(/\.[^./\\]+$/, '');

  // 2. Strip a leading track number ("01 - ", "12. ", "01)").
  s = s.replace(/^\s*\d{1,3}\s*[-.)]\s*/, '');

  // 3. Truncate at the first parenthesised "qualifier" tag. We match
  //    common ones (lyrics, official video, audio, etc.) and chop
  //    everything from that opening bracket onward — that's almost
  //    always either decorative metadata or a stray lyric line.
  const qualifierRe = /\s*[\[(]\s*(?:lyrics?|official\b[^)\]]*|music\s*video|audio|hd|hq|4k|extended|remix|mv|m\/v|cover|live|acoustic|reupload|reuploaded|free\s*download|hq\s*audio|nightcore|sped\s*up|slowed)\b[^)\]]*[\])][\s\S]*$/i;
  s = s.replace(qualifierRe, '');

  s = s.replace(/\s+/g, ' ').trim();

  // 4. Split on a hyphen-with-spaces separator. The plain "-" (no
  //    surrounding spaces) is intentionally ignored — "Boney M." has
  //    a dot, "Mr. Big" has a space-period etc. — only the spaced
  //    dash is a reliable separator in practice.
  const parts = s.split(/\s+[-–—]\s+/);
  if (parts.length === 0) return {};
  if (parts.length === 1) {
    return { title: parts[0].trim() || undefined };
  }
  // For 2 or more chunks, use the last two: many sources prefix with
  // a channel/uploader name ("Majestic - Artist - Title").
  const title = parts[parts.length - 1].trim();
  const artist = parts[parts.length - 2].trim();
  return {
    artist: artist || undefined,
    title: title || undefined,
  };
}

/** Returns the natural pixel size of an image blob. */
export async function getImageDimensions(
  blob: Blob,
): Promise<{ width: number; height: number }> {
  const url = URL.createObjectURL(blob);
  try {
    return await new Promise<{ width: number; height: number }>((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
      img.onerror = () => reject(new Error('Failed to decode image'));
      img.src = url;
    });
  } finally {
    setTimeout(() => URL.revokeObjectURL(url), 0);
  }
}
